/**
 * Subscription Context
 * Fetches and provides the current user's subscription status app-wide.
 * Exposes helpers to check entitlements and enforce free tier limits.
 */

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { API_URL } from '@/lib/config';
import {
  type SubscriptionInfo,
  FREE_TIER_LIMITS,
  ALL_JOURNAL_TYPES,
  ALL_CHAT_MODES,
  isPremium as checkPremium,
} from '@/lib/subscription';

interface SubscriptionContextType {
  /** Current subscription info */
  subscription: SubscriptionInfo;
  /** Whether the user has premium access */
  isPremium: boolean;
  /** Loading state */
  isLoading: boolean;
  /** Number of chat conversations used today */
  dailyChatsUsed: number;
  /** Whether the user can start a new chat conversation */
  canChat: boolean;
  /** Record a chat conversation usage (call after starting a new conversation) */
  recordChatUsage: () => void;
  /** Allowed journal types for the current tier */
  allowedJournalTypes: readonly string[];
  /** Allowed chat modes for the current tier */
  allowedChatModes: readonly string[];
  /** Refresh subscription from server */
  refresh: () => Promise<void>;
}

const defaultSubscription: SubscriptionInfo = {
  plan: 'free',
  status: 'active',
  cancelAtPeriodEnd: false,
};

const SubscriptionContext = createContext<SubscriptionContextType | null>(null);

/** Get today's date key for tracking daily usage */
function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Get daily chat count from localStorage */
function getDailyChats(): number {
  try {
    const stored = localStorage.getItem('jetc_daily_chats');
    if (!stored) return 0;
    const data = JSON.parse(stored) as { date: string; count: number };
    if (data.date !== todayKey()) return 0;
    return data.count;
  } catch {
    return 0;
  }
}

/** Increment daily chat count in localStorage */
function incrementDailyChats(): number {
  const key = todayKey();
  const current = getDailyChats();
  const next = current + 1;
  localStorage.setItem('jetc_daily_chats', JSON.stringify({ date: key, count: next }));
  return next;
}

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const { user, isSignedIn, getToken } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionInfo>(defaultSubscription);
  const [isLoading, setIsLoading] = useState(false);
  const [dailyChatsUsed, setDailyChatsUsed] = useState(getDailyChats);

  const premium = checkPremium(subscription.plan, subscription.status);

  const fetchSubscription = useCallback(async () => {
    if (!isSignedIn) {
      setSubscription(defaultSubscription);
      return;
    }

    setIsLoading(true);
    try {
      const token = await getToken();
      if (!token) return;

      const res = await fetch(`${API_URL}/billing/subscription`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json() as { subscription: Record<string, unknown> };
        const rawPlan = String(data.subscription.plan || 'free');
        const isPaid = rawPlan !== 'free';
        setSubscription({
          plan: isPaid ? 'premium' : 'free',
          status: (data.subscription.status as SubscriptionInfo['status']) || 'active',
          stripePriceId: data.subscription.stripePriceId as string | undefined,
          currentPeriodStart: data.subscription.currentPeriodStart as string | undefined,
          currentPeriodEnd: data.subscription.currentPeriodEnd as string | undefined,
          cancelAtPeriodEnd: data.subscription.cancelAtPeriodEnd as boolean | undefined,
        });
      }
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isSignedIn, getToken]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription, user?.id]);

  const canChat = premium || dailyChatsUsed < FREE_TIER_LIMITS.dailyChatConversations;

  const recordChatUsage = useCallback(() => {
    if (!premium) {
      const next = incrementDailyChats();
      setDailyChatsUsed(next);
    }
  }, [premium]);

  const allowedJournalTypes = premium ? ALL_JOURNAL_TYPES : FREE_TIER_LIMITS.journalTypes;
  const allowedChatModes = premium ? ALL_CHAT_MODES : FREE_TIER_LIMITS.chatModes;

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        isPremium: premium,
        isLoading,
        dailyChatsUsed,
        canChat,
        recordChatUsage,
        allowedJournalTypes,
        allowedChatModes,
        refresh: fetchSubscription,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error('useSubscription must be used within SubscriptionProvider');
  return ctx;
}
